import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { finishedProductsAPI } from '@/utils/api';
import { Plus, Edit, Trash2, ChevronRight, TrendingUp, Crown, Copy, Loader2, Search, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FinishedProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [lockedIds, setLockedIds] = useState([]);
  const [formData, setFormData] = useState({
    name: '', sale_price_public_ttc: '', sale_price_pro_ht: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const [res, planRes] = await Promise.all([
        finishedProductsAPI.getAll(user.id || 'demo-user'),
        fetch(`${API_BASE}/api/user/plan?user_id=${user.id || 'demo-user'}`).then(r => r.json())
      ]);
      setProducts(res.data);
      setLockedIds(planRes.locked_product_ids || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!editingId) {
        const checkRes = await fetch(`${API_BASE}/api/user/can-create?resource=finished_product&user_id=${user.id || 'demo-user'}`);
        const checkData = await checkRes.json();
        if (!checkData.allowed) {
          toast.error(`Limite atteinte (${checkData.current}/${checkData.limit} produits). Passez au plan Premium pour continuer.`, { duration: 5000 });
          return;
        }
      }
      const data = {
        name: formData.name,
        sale_price_public_ttc: parseFloat(formData.sale_price_public_ttc),
        sale_price_pro_ht: parseFloat(formData.sale_price_pro_ht)
      };

      if (editingId) {
        await finishedProductsAPI.update(editingId, data);
        toast.success('Produit mis à jour');
      } else {
        await finishedProductsAPI.create(data, user.id || 'demo-user');
        toast.success('Produit créé');
      }

      setDialogOpen(false);
      setEditingId(null);
      setFormData({ name: '', sale_price_public_ttc: '', sale_price_pro_ht: '' });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur');
    }
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setFormData({
      name: product.name,
      sale_price_public_ttc: product.sale_price_public_ttc,
      sale_price_pro_ht: product.sale_price_pro_ht
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await finishedProductsAPI.delete(id);
      toast.success('Supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDuplicate = async (product) => {
    setDuplicating(product.id);
    try {
      const res = await fetch(`${API_BASE}/api/finished-products/${product.id}/duplicate`, { method: 'POST' });
      if (res.ok) {
        const newProd = await res.json();
        toast.success(`"${newProd.name}" créé avec sa recette`);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur de duplication');
      }
    } catch (e) { toast.error('Erreur de duplication'); }
    finally { setDuplicating(null); }
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-semibold mb-2">Produits Finis</h1>
          <p className="text-muted-foreground">Gestion des produits et rentabilité</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-products"
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-product" onClick={() => { setEditingId(null); setFormData({ name: '', sale_price_public_ttc: '', sale_price_pro_ht: '' }); }}>
              <Plus size={18} className="mr-2" /> Nouveau Produit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Modifier' : 'Nouveau'} Produit Fini</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Nom du Produit</label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Prix Vente Public TTC (€)</label>
                <Input type="number" step="0.01" value={formData.sale_price_public_ttc} onChange={(e) => setFormData({...formData, sale_price_public_ttc: e.target.value})} required />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Prix Vente Pro HT (€)</label>
                <Input type="number" step="0.01" value={formData.sale_price_pro_ht} onChange={(e) => setFormData({...formData, sale_price_pro_ht: e.target.value})} required />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit">{editingId ? 'Mettre à jour' : 'Créer'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())).map(p => {
          const isLocked = lockedIds.includes(p.id);
          return (
          <Card key={p.id} className={`transition-all ${isLocked ? 'opacity-50 border-gray-300' : 'hover:border-primary/20'}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="flex items-center gap-2">
                  {isLocked && <Lock size={16} className="text-gray-500" />}
                  {p.name}
                </CardTitle>
                <div className="flex gap-2">
                  {isLocked ? (
                    <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded flex items-center gap-1"><Lock size={12} /> Verrouillé</span>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" title="Dupliquer avec recette" data-testid={`btn-duplicate-${p.id}`}
                        onClick={() => handleDuplicate(p)} disabled={duplicating === p.id}>
                        {duplicating === p.id ? <Loader2 size={16} className="animate-spin" /> : <Copy size={16} />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEdit(p)}><Edit size={16} /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(p.id)}><Trash2 size={16} /></Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Prix Public TTC</p>
                  <p className="font-semibold text-lg">{p.sale_price_public_ttc.toFixed(2)}€</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Prix Pro HT</p>
                  <p className="font-semibold text-lg">{p.sale_price_pro_ht.toFixed(2)}€</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Coût de Revient</p>
                  <p className="font-semibold">{p.total_cost.toFixed(2)}€</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Coef. Public</p>
                  <p className="font-semibold">{p.coefficient_public.toFixed(2)}x</p>
                </div>
              </div>
              
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={16} className="text-green-600" />
                  <span className="text-xs font-semibold text-green-800">MARGE BRUTE PUBLIC</span>
                </div>
                <p className="text-2xl font-bold text-green-600">+{p.gross_margin_public.toFixed(2)}€</p>
                <p className="text-xs text-green-700 mt-1">Marge Pro: +{p.gross_margin_pro.toFixed(2)}€</p>
              </div>

              {!isLocked && (
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => navigate(`/production/product/${p.id}/recipe`)}
                >
                  Gérer la Recette <ChevronRight size={16} className="ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>
          );
        })}
      </div>

      {products.length === 0 && (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Aucun produit fini</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" /> Créer le premier produit
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default FinishedProducts;
